import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { CrossingGap, findOrthogonalCrossings } from './crossingGaps';
import { OrthogonalPathPoint } from './orthogonalPath';

interface MeasuredPath {
  points: OrthogonalPathPoint[];
  borderRadius: number;
}

interface LiveEdgeCrossings {
  crossings: Map<string, CrossingGap[]>;
  setPath: (id: string, path: MeasuredPath | null) => void;
}

const LiveEdgeCrossingsContext = createContext<LiveEdgeCrossings | null>(null);

/** Share measured paths within one diagram, including edges that did not move. */
export function LiveEdgeCrossingsProvider({ children }: PropsWithChildren) {
  const [paths, setPaths] = useState(new Map<string, MeasuredPath>());
  const setPath = useCallback((id: string, path: MeasuredPath | null) => {
    setPaths((previous) => {
      if (
        (path &&
          previous.get(id)?.points === path.points &&
          previous.get(id)?.borderRadius === path.borderRadius) ||
        (!path && !previous.has(id))
      )
        return previous;
      const next = new Map(previous);
      if (path) next.set(id, path);
      else next.delete(id);
      return next;
    });
  }, []);
  const value = useMemo(
    () => ({
      crossings: findOrthogonalCrossings(
        new Map(Array.from(paths, ([id, path]) => [id, path.points])),
        new Map(Array.from(paths, ([id, path]) => [id, path.borderRadius]))
      ),
      setPath,
    }),
    [paths, setPath]
  );
  return (
    <LiveEdgeCrossingsContext.Provider value={value}>
      {children}
    </LiveEdgeCrossingsContext.Provider>
  );
}

/** Update gaps before paint whenever measured handles or route waypoints change. */
export function useLiveEdgeCrossings(
  id: string,
  points: OrthogonalPathPoint[],
  enabled: boolean,
  fallback: OrthogonalPathPoint[] | undefined,
  borderRadius: number
) {
  const context = useContext(LiveEdgeCrossingsContext);
  const setPath = context?.setPath;
  useLayoutEffect(() => {
    setPath?.(id, enabled ? { points, borderRadius } : null);
  }, [id, points, borderRadius, enabled, setPath]);
  useLayoutEffect(() => () => setPath?.(id, null), [id, setPath]);
  return enabled && context ? context.crossings.get(id) : fallback;
}
